import { action, computed, makeObservable, observable } from 'mobx'

import { drawCursor } from '~/client/src/shared/components/MapMarkups/markupCursor'
import { DEFAULT_SHAPE_PX } from '~/client/src/shared/components/MapMarkups/markupTools'

import type PhotoMarkupStore from './PhotoMarkup.store'
import { eraseAlong } from './photoMarkupEraser'
import {
  expandRect,
  isPointInRect,
  markupBounds,
  moveVertex,
  resizeMarkup,
  snapToAngle,
  squareAround,
  textOrigin,
  topmostHit,
  translateMarkup,
} from './photoMarkupGeometry'
import {
  IPathMarkup,
  IPoint,
  isTextMarkup,
  isVertexEdited,
  PhotoMarkup,
} from './photoMarkupModel'

export interface IPointerInput {
  point: IPoint
  shiftKey: boolean
  timeStamp: number
}

interface IGestureStart {
  start: IPoint
  startScreen: IPoint
}

interface IRestorePoint {
  markups: PhotoMarkup[]
  selectedId: string | null
  editingId: string | null
}

type Gesture = IGestureStart &
  (
    | { type: 'draw' }
    | { type: 'erase'; last: IPoint }
    | { type: 'tap'; id?: string }
    | { type: 'move'; id: string; original: PhotoMarkup; hasMoved: boolean }
    | { type: 'resize'; corner: number; original: PhotoMarkup }
    | { type: 'vertex'; index: number; original: IPathMarkup }
    | { type: 'pan'; last: IPoint }
  )

const HIT_TOLERANCE_PX = 6
const MOBILE_HIT_TOLERANCE_PX = 12
const HANDLE_HIT_PX = 12
const MOBILE_HANDLE_HIT_PX = 20
const CLICK_THRESHOLD_PX = 4
const MOBILE_TAP_SLOP_PX = 10
const MIN_EXTENT_PX = 4
const VERTEX_CLOSE_PX = 10
const FREEHAND_SPACING_PX = 1
const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_PX = 24
const MIN_POLYGON_VERTICES = 3
const MIN_POLYLINE_VERTICES = 2
const PREVIEW_ID = 'preview'

function distance(a: IPoint, b: IPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

export default class PhotoMarkupGestures {
  @observable.ref public vertices: IPoint[] = []
  @observable.ref public segmentAnchor: IPoint | null = null
  @observable.ref public draft: IPathMarkup | null = null
  @observable.ref public hoverPoint: IPoint | null = null
  @observable public interacting = false
  @observable public isPointerDown = false
  private gesture: Gesture | null = null
  private restorePoint: IRestorePoint | null = null
  private lastTap: { id: string; point: IPoint; timeStamp: number } | null =
    null

  public constructor(private readonly store: PhotoMarkupStore) {
    makeObservable(this)
  }

  @computed
  public get isActive(): boolean {
    return this.isPointerDown || !!this.vertices.length || !!this.segmentAnchor
  }

  @computed
  public get hasPendingMarkup(): boolean {
    const min =
      this.store.activeTool === 'shape'
        ? MIN_POLYGON_VERTICES
        : MIN_POLYLINE_VERTICES
    return this.vertices.length >= min
  }

  @computed
  public get preview(): IPathMarkup | null {
    if (this.draft) return this.draft
    if (!this.vertices.length && !this.segmentAnchor) return null
    const hover =
      this.hoverPoint && this.store.view
        ? this.store.toImage(this.hoverPoint)
        : null
    if (this.vertices.length) {
      const kind = this.store.activeTool === 'shape' ? 'shape' : 'arrow'
      const points = hover ? [...this.vertices, hover] : this.vertices
      return this.store.newPath(kind, points, PREVIEW_ID)
    }
    if (this.segmentAnchor && hover) {
      return this.store.newPath(
        'arrow',
        [this.segmentAnchor, hover],
        PREVIEW_ID,
      )
    }
    return null
  }

  @computed
  public get vertexScreens(): IPoint[] {
    if (!this.store.view) return []
    const points = this.segmentAnchor ? [this.segmentAnchor] : this.vertices
    return points.map(p => this.store.toScreen(p))
  }

  @computed
  public get cursor(): string {
    const { activeTool, drawType, unit, view, style } = this.store
    switch (activeTool) {
      case 'draw':
        return drawCursor(
          drawType,
          style.strokeWidth * unit * (view?.scale ?? 1),
        )
      case 'shape':
      case 'arrow':
        return this.hoverPoint && this.isNearFirstVertex(this.hoverPoint)
          ? 'pointer'
          : 'crosshair'
      case 'text':
        return 'text'
    }
    return this.selectCursor()
  }

  private get tapSlop(): number {
    return this.store.isMobileEnv ? MOBILE_TAP_SLOP_PX : CLICK_THRESHOLD_PX
  }

  private get hitTolerance(): number {
    const px = this.store.isMobileEnv
      ? MOBILE_HIT_TOLERANCE_PX
      : HIT_TOLERANCE_PX
    return px * this.pxToImage
  }

  private get pxToImage(): number {
    return 1 / (this.store.view?.scale ?? 1)
  }

  private isDrag(from: IPoint, to: IPoint): boolean {
    return distance(from, to) > this.tapSlop
  }

  private isNearFirstVertex(screen: IPoint): boolean {
    return (
      this.store.activeTool === 'shape' &&
      this.vertices.length >= 3 &&
      distance(this.store.toScreen(this.vertices[0]), screen) <= VERTEX_CLOSE_PX
    )
  }

  private handleAt(screen: IPoint): number | null {
    const reach = this.store.isMobileEnv ? MOBILE_HANDLE_HIT_PX : HANDLE_HIT_PX
    const index = this.store.selectionHandles.findIndex(
      handle => distance(handle, screen) <= reach,
    )
    return index === -1 ? null : index
  }

  private hitAt(point: IPoint): PhotoMarkup | null {
    const { markups, unit, measure } = this.store
    return topmostHit(markups, point, this.hitTolerance, unit, measure)
  }

  private isInsideSelection(point: IPoint): boolean {
    const { selected, unit, measure } = this.store
    if (!selected) return false
    const bounds = markupBounds(selected, unit, measure)
    return isPointInRect(point, expandRect(bounds, this.hitTolerance))
  }

  private selectCursor(): string {
    const { selected, view } = this.store
    if (this.interacting) return 'move'
    if (!this.hoverPoint || !view) return 'default'
    const handle = this.handleAt(this.hoverPoint)
    if (selected && handle != null) {
      if (isVertexEdited(selected)) return 'move'
      return handle % 2 ? 'nesw-resize' : 'nwse-resize'
    }
    const point = this.store.toImage(this.hoverPoint)
    const hit = this.hitAt(point)
    if (hit && hit.id !== selected?.id) return 'pointer'
    if (hit || this.isInsideSelection(point)) return 'move'
    return this.store.isZoomed ? 'grab' : 'default'
  }

  @action.bound
  public pointerDown(input: IPointerInput): void {
    const { store } = this
    if (!store.view || store.isSaving) return
    this.hoverPoint = input.point
    this.restorePoint = {
      markups: store.markups,
      selectedId: store.selectedId,
      editingId: store.editingId,
    }
    store.finishTextEditing()
    const start = {
      start: store.toImage(input.point),
      startScreen: input.point,
    }
    this.isPointerDown = true
    this.gesture = this.beginGesture(start, input)
  }

  private beginGesture(start: IGestureStart, input: IPointerInput): Gesture {
    const { store } = this
    const point = start.start
    switch (store.activeTool) {
      case 'select':
        return this.beginSelectGesture(start, input)
      case 'draw':
        if (store.drawType === 'eraser') {
          this.eraseAlong(point, point)
          return { ...start, type: 'erase', last: point }
        }
        this.draft = store.newPath(
          store.drawType === 'highlighter' ? 'highlighter' : 'freehand',
          [point],
        )
        return { ...start, type: 'draw' }
      case 'shape':
        if (store.shapeKind === 'polygon') return { ...start, type: 'tap' }
        this.draft = store.newPath('shape', [point, point])
        return { ...start, type: 'draw' }
      case 'arrow':
        if (store.lineType === 'polyline') return { ...start, type: 'tap' }
        this.draft = store.newPath('arrow', [point, point])
        return { ...start, type: 'draw' }
      default:
        return { ...start, type: 'tap' }
    }
  }

  private beginSelectGesture(
    start: IGestureStart,
    input: IPointerInput,
  ): Gesture | null {
    const { store } = this
    const selected = store.selected
    const handle = this.handleAt(input.point)
    if (selected && handle != null) {
      return isVertexEdited(selected)
        ? {
            ...start,
            type: 'vertex',
            index: handle,
            original: selected as IPathMarkup,
          }
        : { ...start, type: 'resize', corner: handle, original: selected }
    }
    const hit = this.hitAt(start.start)
    if (hit && hit.id !== selected?.id) {
      store.select(hit.id)
      return { ...start, type: 'tap', id: hit.id }
    }
    if (selected && (hit || this.isInsideSelection(start.start))) {
      return {
        ...start,
        type: 'move',
        id: selected.id,
        original: selected,
        hasMoved: false,
      }
    }
    store.select(null)
    return store.isZoomed ? { ...start, type: 'pan', last: input.point } : null
  }

  @action.bound
  public pointerMove(input: IPointerInput): void {
    this.hoverPoint = input.point
    const gesture = this.gesture
    if (!gesture || !this.store.view) return
    const { store } = this
    const point = store.toImage(input.point)
    switch (gesture.type) {
      case 'draw':
        this.updateDraft(gesture, point, input.shiftKey)
        return
      case 'erase':
        this.eraseAlong(gesture.last, point)
        gesture.last = point
        return
      case 'move':
        if (!gesture.hasMoved && !this.isDrag(gesture.startScreen, input.point))
          return
        gesture.hasMoved = true
        this.interacting = true
        store.replaceMarkup(
          translateMarkup(
            gesture.original,
            point.x - gesture.start.x,
            point.y - gesture.start.y,
          ),
        )
        return
      case 'resize':
        this.interacting = true
        store.replaceMarkup(
          resizeMarkup(
            gesture.original,
            gesture.corner,
            point,
            MIN_EXTENT_PX * this.pxToImage,
            store.unit,
            store.measure,
          ),
        )
        return
      case 'pan':
        store.zoomView(gesture.last, input.point, 1)
        gesture.last = input.point
        return
      case 'vertex': {
        this.interacting = true
        const { points } = gesture.original
        const neighbor = points[gesture.index ? gesture.index - 1 : 1]
        const target =
          input.shiftKey && neighbor ? snapToAngle(neighbor, point) : point
        store.replaceMarkup(moveVertex(gesture.original, gesture.index, target))
      }
    }
  }

  @action.bound
  public pointerUp(input: IPointerInput): void {
    const gesture = this.gesture
    this.gesture = null
    this.restorePoint = null
    this.isPointerDown = false
    this.interacting = false
    if (!gesture || !this.store.view) return
    const point = this.store.toImage(input.point)
    switch (gesture.type) {
      case 'draw':
        this.finishDraw(gesture, point, input)
        return
      case 'move':
        if (gesture.hasMoved) this.store.history.commit()
        else this.registerTap(gesture.id, input)
        return
      case 'tap':
        this.finishTap(gesture, point, input)
        return
      default:
        this.store.history.commit()
    }
  }

  @action.bound
  public pointerCancel(): void {
    this.abort()
  }

  @action.bound
  public abort(): void {
    const restorePoint = this.restorePoint
    if (restorePoint) {
      this.store.setMarkups(restorePoint.markups)
      if (restorePoint.editingId) {
        this.store.startEditing(restorePoint.editingId)
      } else {
        this.store.select(restorePoint.selectedId)
      }
    }
    this.restorePoint = null
    this.gesture = null
    this.draft = null
    this.hoverPoint = null
    this.isPointerDown = false
    this.interacting = false
  }

  @action.bound
  public pointerLeave(): void {
    if (!this.gesture) this.hoverPoint = null
  }

  @action.bound
  public reset(): void {
    this.gesture = null
    this.restorePoint = null
    this.draft = null
    this.vertices = []
    this.segmentAnchor = null
    this.isPointerDown = false
    this.interacting = false
  }

  @action.bound
  public cancelSegment(): void {
    this.segmentAnchor = null
  }

  @action.bound
  public cancelVertices(): void {
    this.vertices = []
    this.store.showSelectTool()
  }

  @action.bound
  public finishPolygon(): void {
    if (this.vertices.length < MIN_POLYGON_VERTICES) {
      this.cancelVertices()
      return
    }
    const markup = this.store.newPath('shape', this.vertices)
    this.vertices = []
    this.store.finishDiscreteDraw(markup)
  }

  @action.bound
  public finishPolyline(): void {
    if (this.vertices.length < MIN_POLYLINE_VERTICES) {
      this.cancelVertices()
      return
    }
    const markup = this.store.newPath('arrow', this.vertices)
    this.vertices = []
    this.store.finishDiscreteDraw(markup)
  }

  private updateDraft(gesture: Gesture, point: IPoint, shiftKey: boolean) {
    const draft = this.draft
    if (!draft) return
    if (draft.kind === 'freehand' || draft.kind === 'highlighter') {
      const last = draft.points[draft.points.length - 1]
      if (distance(last, point) < FREEHAND_SPACING_PX * this.pxToImage) return
      this.draft = { ...draft, points: [...draft.points, point] }
      return
    }
    const end =
      draft.kind === 'arrow' && shiftKey
        ? snapToAngle(gesture.start, point)
        : point
    this.draft = { ...draft, points: [gesture.start, end] }
  }

  private finishDraw(gesture: Gesture, point: IPoint, input: IPointerInput) {
    const { store } = this
    const draft = this.draft
    this.draft = null
    if (!draft) return
    if (draft.kind === 'freehand' || draft.kind === 'highlighter') {
      if (draft.points.length < 2) return
      store.addMarkup(draft)
      store.history.commit()
      return
    }
    const isTap = !this.isDrag(gesture.startScreen, input.point)
    if (draft.kind === 'shape') {
      const points = isTap
        ? squareAround(gesture.start, (DEFAULT_SHAPE_PX / 2) * this.pxToImage)
        : [gesture.start, point]
      store.finishDiscreteDraw({ ...draft, points })
      return
    }
    const anchor = isTap ? this.segmentAnchor : gesture.start
    if (!anchor) {
      this.segmentAnchor = gesture.start
      return
    }
    if (isTap && !this.isDrag(store.toScreen(anchor), input.point)) return
    this.segmentAnchor = null
    const end = input.shiftKey ? snapToAngle(anchor, point) : point
    store.finishDiscreteDraw({ ...draft, points: [anchor, end] })
  }

  private finishTap(gesture: Gesture, point: IPoint, input: IPointerInput) {
    const { store } = this
    if (gesture.type === 'tap' && gesture.id) {
      this.registerTap(gesture.id, input)
      return
    }
    if (store.activeTool === 'text') {
      store.placeText(
        textOrigin(gesture.start, store.style.textStyle.fontSize, store.unit),
      )
      return
    }
    if (store.activeTool === 'shape' || store.activeTool === 'arrow') {
      this.addVertex(point, input)
    }
  }

  private addVertex(point: IPoint, input: IPointerInput): void {
    const last = this.vertices[this.vertices.length - 1]
    if (last && !this.isDrag(this.store.toScreen(last), input.point)) return
    if (this.isNearFirstVertex(input.point)) {
      this.finishPolygon()
      return
    }
    const vertex = last && input.shiftKey ? snapToAngle(last, point) : point
    this.vertices = [...this.vertices, vertex]
  }

  private registerTap(id: string, input: IPointerInput): void {
    const last = this.lastTap
    const isDoubleTap =
      !!last &&
      last.id === id &&
      input.timeStamp - last.timeStamp < DOUBLE_TAP_MS &&
      distance(last.point, input.point) < DOUBLE_TAP_PX
    this.lastTap = isDoubleTap
      ? null
      : { id, point: input.point, timeStamp: input.timeStamp }
    const markup = this.store.markups.find(m => m.id === id)
    if (isDoubleTap && markup && isTextMarkup(markup)) {
      this.store.startEditing(id)
    }
  }

  private eraseAlong(from: IPoint, to: IPoint): void {
    const { store } = this
    const width = store.style.strokeWidth * store.unit
    const radius = Math.max(this.pxToImage, width / 2)
    const next = eraseAlong(store.markups, from, to, radius, store.createId)
    if (next !== store.markups) store.setMarkups(next)
  }
}
